import { useState, useCallback, useEffect, useMemo } from 'react'
import { X, Plus, Trash2, Play, Pause, RotateCw, Save, Clock } from 'lucide-react'
import { ModelViewer, type ModelSource } from '../ModelViewer'
import type { AnimationKeyframe, ShowcaseAnimation, CameraState } from '@/types/camera'
import type { Quaternion } from '@/utils/quaternion'
import { quaternionToCameraState, cameraStateToQuaternion } from '@/utils/cameraConversion'
import { getCameraAtTime } from '@/utils/cameraAnimation'
import { nanoid } from 'nanoid'

interface ShowcaseAnimationEditorProps {
  modelId: string
  modelUrl: string
  filePath?: string
  isPlyModel?: boolean
  sequenceInfo?: { frameUrls: string[]; fps: number; audioUrl?: string | null } | null
  /** Audio URL for single-file (.splatv / .ply) playback. Sequences pull
   *  their audio URL from sequenceInfo.audioUrl. */
  audioUrl?: string | null
  /** Fully resolved source (e.g. from resolveModelSource). When provided it
   *  wins over modelUrl / isPlyModel / sequenceInfo routing — needed for
   *  ClipGStream models, which those legacy props can't describe. */
  source?: ModelSource | null
  currentAnimation?: ShowcaseAnimation
  defaultCamera?: CameraState
  onSave: (animation: ShowcaseAnimation) => void
  onCancel: () => void
}

export function ShowcaseAnimationEditor({
  modelId: _modelId,
  modelUrl,
  filePath,
  isPlyModel,
  sequenceInfo = null,
  audioUrl = null,
  source: sourceProp = null,
  currentAnimation,
  defaultCamera,
  onSave,
  onCancel
}: ShowcaseAnimationEditorProps) {
  // Fall back to filePath sniffing for callers that don't pass isPlyModel.
  const usePlyViewer = isPlyModel ?? !!filePath?.endsWith('.ply')
  // Sequences always route to DualgsViewer regardless of usePlyViewer —
  // SplatViewer can't decode per-frame .ply URLs.
  const source = useMemo<ModelSource>(() => sourceProp ?? {
    kind: sequenceInfo ? 'sequence' : usePlyViewer ? 'ply' : 'single',
    modelUrl,
    audioUrl: audioUrl ?? null,
    sequenceInfo,
    dataUrl: null,
    // Legacy callers can't describe a scene layer; only a resolved source can.
    staticUrl: null,
    staticTransform: null,
  }, [sourceProp, sequenceInfo, usePlyViewer, modelUrl, audioUrl])
  const [keyframes, setKeyframes] = useState<AnimationKeyframe[]>(() => {
    if (currentAnimation?.keyframes && currentAnimation.keyframes.length > 0) {
      return currentAnimation.keyframes
    }
    // If no existing keyframes and we have a defaultCamera, create an initial keyframe
    if (defaultCamera) {
      return [{
        id: nanoid(),
        time: 0,
        camera: defaultCamera
      }]
    }
    return []
  })
  const [duration, setDuration] = useState(currentAnimation?.duration || 10)
  const [loop, setLoop] = useState(currentAnimation?.loop ?? true)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [selectedKeyframe, setSelectedKeyframe] = useState<string | null>(null)
  
  // Camera state for controlling the viewer
  const [cameraPosition, setCameraPosition] = useState<[number, number, number]>(() => {
    if (defaultCamera) {
      const { position } = cameraStateToQuaternion(defaultCamera)
      return position
    }
    return [0, 0, 1]
  })
  
  const [cameraQuaternion, setCameraQuaternion] = useState<Quaternion>(() => {
    if (defaultCamera) {
      const { quaternion } = cameraStateToQuaternion(defaultCamera)
      return quaternion
    }
    return { w: 1, x: 0, y: 0, z: 0 }
  })

  // Handle camera changes from viewer
  const handleCameraChange = useCallback((position: [number, number, number], quaternion: Quaternion) => {
    // Don't update during playback
    if (isPlaying) return
    
    setCameraPosition(position)
    setCameraQuaternion(quaternion)

    if (process.env.NODE_ENV === 'development') {
      console.trace('handleCameraChange called with:', { position, quaternion, isPlaying })
    }    
  }, [isPlaying])

  // Add new keyframe
  const addKeyframe = () => {
    const newKeyframe: AnimationKeyframe = {
      id: nanoid(),
      time: currentTime,
      camera: quaternionToCameraState(cameraPosition, cameraQuaternion)
    }
    
    const updatedKeyframes = [...keyframes, newKeyframe].sort((a, b) => a.time - b.time)
    setKeyframes(updatedKeyframes)
    setSelectedKeyframe(newKeyframe.id)
  }

  // Update keyframe
  const updateKeyframe = (id: string, updates: Partial<AnimationKeyframe>) => {
    setKeyframes(keyframes.map(kf => 
      kf.id === id ? { ...kf, ...updates } : kf
    ).sort((a, b) => a.time - b.time))
  }

  // Delete keyframe
  const deleteKeyframe = (id: string) => {
    setKeyframes(keyframes.filter(kf => kf.id !== id))
    if (selectedKeyframe === id) {
      setSelectedKeyframe(null)
    }
  }

  // Use current camera for keyframe
  const useCurrentCameraForKeyframe = (id: string) => {
    updateKeyframe(id, {
      camera: quaternionToCameraState(cameraPosition, cameraQuaternion)
    })
  }

  // Update camera based on current time and keyframes
  const updateCameraFromKeyframes = useCallback(() => {
    if (keyframes.length === 0) return
    
    const cameraState = getCameraAtTime(keyframes, currentTime)
    const { position, quaternion } = cameraStateToQuaternion(cameraState)
    
    setCameraPosition(position)
    setCameraQuaternion(quaternion)
  }, [keyframes, currentTime])

  // Handle playback animation
  useEffect(() => {
    if (!isPlaying || keyframes.length < 2) return
    
    let animationFrame: number
    let startTime: number
    const playStartTime = currentTime
    
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const elapsed = (timestamp - startTime) / 1000 // Convert to seconds
      const newTime = playStartTime + elapsed
      
      if (newTime >= duration) {
        if (loop) {
          // Loop back to start
          setCurrentTime(0)
          startTime = timestamp
        } else {
          // Stop at end
          setCurrentTime(duration)
          setIsPlaying(false)
          return
        }
      } else {
        setCurrentTime(newTime)
      }
      
      animationFrame = requestAnimationFrame(animate)
    }
    
    animationFrame = requestAnimationFrame(animate)
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [isPlaying, keyframes, duration, loop, currentTime])
  
  // Update camera when time changes during playback
  useEffect(() => {
    if (isPlaying && keyframes.length > 0) {
      updateCameraFromKeyframes()
    }
  }, [currentTime, isPlaying, keyframes.length, updateCameraFromKeyframes])

  // Play/pause animation
  const togglePlayback = () => {
    setIsPlaying(!isPlaying)
  }

  // Reset animation
  const resetAnimation = () => {
    setCurrentTime(0)
    setIsPlaying(false)
    if (keyframes.length > 0) {
      updateCameraFromKeyframes()
    }
  }

  // Handle save
  const handleSave = () => {
    onSave({
      keyframes,
      duration,
      loop
    })
  }

  // Timeline scrubber
  const handleTimeChange = (time: number) => {
    setCurrentTime(time)
    setIsPlaying(false)
    if (keyframes.length > 0) {
      // Update camera to match the new time
      const cameraState = getCameraAtTime(keyframes, time)
      const { position, quaternion } = cameraStateToQuaternion(cameraState)
      setCameraPosition(position)
      setCameraQuaternion(quaternion)
      console.log('handleTimeChange-cameraQuaternion', quaternion)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Showcase Animation Editor</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex min-h-0">
          {/* Viewer */}
          <div className="flex-1 p-6">
            <div className="h-full bg-gray-100 rounded-lg overflow-hidden">
              <ModelViewer
                source={source}
                className="w-full h-full"
                cameraPosition={cameraPosition}
                cameraQuaternion={cameraQuaternion}
                onCameraChange={handleCameraChange}
                dualgs={{
                  showUi: false,
                  showTimeline: !!source.sequenceInfo,
                }}
                splat={{
                  controls: true,
                  quality: 'high',
                  defaultCamera,
                  autoFocus: !defaultCamera,
                }}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="w-96 p-6 border-l overflow-y-auto">
            <div className="space-y-6">
              {/* Animation Settings */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Animation Settings</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-600 flex justify-between">
                      <span>Duration (seconds)</span>
                      <span>{duration}s</span>
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="60"
                      step="1"
                      value={duration}
                      onChange={(e) => setDuration(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={loop}
                      onChange={(e) => setLoop(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700">Loop animation</span>
                  </label>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Timeline</h3>
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="range"
                      min="0"
                      max={duration}
                      step="0.1"
                      value={currentTime}
                      onChange={(e) => handleTimeChange(Number(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>0s</span>
                      <span>{currentTime.toFixed(1)}s</span>
                      <span>{duration}s</span>
                    </div>
                    {/* Keyframe markers */}
                    <div className="absolute inset-x-0 top-0 h-2 pointer-events-none">
                      {keyframes.map(kf => (
                        <div
                          key={kf.id}
                          className={`absolute w-2 h-2 -mt-1 rounded-full ${
                            selectedKeyframe === kf.id ? 'bg-indigo-600' : 'bg-gray-400'
                          }`}
                          style={{ left: `${(kf.time / duration) * 100}%` }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Playback controls */}
                  <div className="flex gap-2">
                    <button
                      onClick={togglePlayback}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                      {isPlaying ? 'Pause' : 'Play'}
                    </button>
                    <button
                      onClick={resetAnimation}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <RotateCw size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Keyframes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900">Keyframes</h3>
                  <button
                    onClick={addKeyframe}
                    className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Plus size={14} />
                    Add Keyframe
                  </button>
                </div>
                
                <div className="space-y-2">
                  {keyframes.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No keyframes yet. Add keyframes to create an animation.
                    </p>
                  ) : (
                    keyframes.map((kf, index) => (
                      <div
                        key={kf.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedKeyframe === kf.id
                            ? 'border-indigo-600 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedKeyframe(kf.id)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Keyframe {index + 1}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              deleteKeyframe(kf.id)
                            }}
                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Clock size={14} className="text-gray-400" />
                            <input
                              type="number"
                              min="0"
                              max={duration}
                              step="0.1"
                              value={kf.time}
                              onChange={(e) => updateKeyframe(kf.id, { time: Number(e.target.value) })}
                              onClick={(e) => e.stopPropagation()}
                              className="px-2 py-1 text-sm border rounded w-20"
                            />
                            <span className="text-sm text-gray-500">seconds</span>
                          </div>
                          
                          {selectedKeyframe === kf.id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                useCurrentCameraForKeyframe(kf.id)
                              }}
                              className="w-full text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                            >
                              Use Current Camera
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Tips</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Position the camera and add keyframes at different times</li>
                  <li>• The camera will smoothly animate between keyframes</li>
                  <li>• Preview the animation with the play button</li>
                  <li>• At least 2 keyframes are needed for animation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={keyframes.length < 2}
            className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            Save Animation
          </button>
        </div>
      </div>
    </div>
  )
}